import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Avatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Close, Business, LocationOn, Email, Phone, Language, Receipt, CheckCircle, Cancel, People, Star } from '@mui/icons-material';
import { Company, ProductPlan } from '../../Interfaces/Company';
import { apiClient } from '../../../utils/ApiClient';
import { Logger } from '../../../utils/Logger';

interface CompanyViewDialogProps {
  open: boolean;
  onClose: () => void;
  company: Company | null;
}

// Helper function to format date
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

// Helper function to check if plan is active
const isPlanActive = (startDate: string, endDate: string): boolean => {
  if (!startDate || !endDate) return false;
  
  try {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return now >= start && now <= end;
  } catch (error) {
    return false;
  }
};

interface CompanyMember {
  _id: string;
  email: string;
  mobile: string;
  status: string;
  isPrimary: boolean;
  moduleAndRole: Array<{ module: string; role: string }>;
  profile: {
    firstName: string;
    lastName: string;
    avatar: string;
    hasProfileImage: boolean;
  };
}

export const CompanyViewDialog: React.FC<CompanyViewDialogProps> = ({
  open,
  onClose,
  company
}) => {
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState('');

  const fetchMembers = useCallback(async (companyId: string) => {
    setMembersLoading(true);
    setMembersError('');
    try {
      const result = await apiClient.get(`/company/${companyId}/members`);
      const data = result.data || result;
      setMembers(Array.isArray(data) ? data : []);
    } catch (err: any) {
      Logger.error('CompanyViewDialog', 'Failed to fetch company members: ' + err?.message);
      setMembersError('Failed to load company members');
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && company?._id) {
      fetchMembers(company._id);
    } else {
      setMembers([]);
      setMembersError('');
    }
  }, [open, company?._id, fetchMembers]);

  if (!company) return null;

  const getInitials = (firstName: string, lastName: string): string => {
    return `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase() || '?';
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const bufferLikeToDataUrl = (value: any): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') return value; // already a data URL or URL
    if (value?.type === 'Buffer' && Array.isArray(value?.data)) {
      try {
        const u8 = new Uint8Array(value.data as number[]);
        let binary = '';
        for (let i = 0; i < u8.length; i++) binary += String.fromCharCode(u8[i]);
        const base64 = btoa(binary);
        return `data:image/png;base64,${base64}`;
      } catch (e) {
        return undefined;
      }
    }
    return undefined;
  };

  const lightLogo = bufferLikeToDataUrl((company as any)?.brand?.logo?.light);
  const darkLogo = bufferLikeToDataUrl((company as any)?.brand?.logo?.dark);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Business color="primary" />
          <Typography variant="h5" component="span">
            Company Details
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={3}>
          {/* Company ID */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Company ID
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontFamily: 'monospace', 
                  fontSize: '0.875rem'
                }}
              >
                {company._id}
              </Typography>
            </Box>
          </Grid>

          {/* Basic Information */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business color="primary" />
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Company Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {company.name || 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Website
                    </Typography>
                    <Typography variant="body1">
                      {company.website || 'N/A'}
                    </Typography>
                  </Box>
                  
                  
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Tax Code
                    </Typography>
                    <Typography variant="body1">
                      {company.taxcode || 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Registration Number
                    </Typography>
                    <Typography variant="body1">
                      {company.registrationNumber || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email color="primary" />
                  Contact Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {company.companyEmail || 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Phone
                    </Typography>
                    <Typography variant="body1">
                      {company.companyPhone || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Brand Logos */}
          {(lightLogo || darkLogo) && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Brand Logos
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                    {lightLogo && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">Light</Typography>
                        <Box component="img" alt="Light Logo" src={lightLogo} sx={{ height: 70, width: 'auto', maxWidth: '100%', objectFit: 'contain' }} />
                      </Box>
                    )}
                    {darkLogo && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">Dark</Typography>
                        <Box sx={{ p: 1, bgcolor: 'grey.900', borderRadius: 1 }}>
                          <Box component="img" alt="Dark Logo" src={darkLogo} sx={{ height: 70, width: 'auto', maxWidth: '100%', objectFit: 'contain' }} />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Address Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn color="primary" />
                  Address Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Address Line 1
                      </Typography>
                      <Typography variant="body1">
                        {company.addressLine1 || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Address Line 2
                      </Typography>
                      <Typography variant="body1">
                        {company.addressLine2 || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        City
                      </Typography>
                      <Typography variant="body1">
                        {company.city || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        State
                      </Typography>
                      <Typography variant="body1">
                        {company.state || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Country
                      </Typography>
                      <Typography variant="body1">
                        {company.country || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        ZIP Code
                      </Typography>
                      <Typography variant="body1">
                        {company.zip || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Product Plans */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Receipt color="primary" />
                  Product Plans
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {!company.productPlans || company.productPlans.length === 0 ? (
                  <Box sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic', py: 2 }}>
                    No product plans available
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {company.productPlans.map((plan: ProductPlan, index: number) => {
                      const key = plan._id || `${plan.module}-${plan.licenseType}-${index}`;
                      const isActive = isPlanActive(plan.startedAt, plan.endsAt);
                      
                      return (
                        <Grid item xs={12} md={6} key={key}>
                          <Card 
                            variant="outlined" 
                            sx={{ 
                              borderColor: isActive ? 'success.main' : 'error.main',
                              '&:hover': {
                                boxShadow: 2,
                                borderColor: isActive ? 'success.dark' : 'error.dark'
                              }
                            }}
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                                  {plan.module || 'N/A'}
                                </Typography>
                                <Chip
                                  label={isActive ? 'ACTIVE' : 'INACTIVE'}
                                  size="small"
                                  color={isActive ? 'success' : 'error'}
                                  variant="outlined"
                                  icon={isActive ? <CheckCircle /> : <Cancel />}
                                />
                              </Box>
                              
                              <Grid container spacing={1}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    License Type:
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {plan.licenseType || 'N/A'}
                                  </Typography>
                                </Grid>
                                
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    Users:
                                  </Typography>
                                  <Typography variant="body2">
                                    {plan.noOfUsers || 'N/A'}
                                  </Typography>
                                </Grid>
                                
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    Start Date:
                                  </Typography>
                                  <Typography variant="body2">
                                    {formatDate(plan.startedAt)}
                                  </Typography>
                                </Grid>
                                
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    End Date:
                                  </Typography>
                                  <Typography variant="body2">
                                    {formatDate(plan.endsAt)}
                                  </Typography>
                                </Grid>
                                
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    Status:
                                  </Typography>
                                  <Typography variant="body2">
                                    {plan.status || 'N/A'}
                                  </Typography>
                                </Grid>

                                {/* Quotas - show UNLIMITED if not set */}
                                <Grid item xs={12}>
                                  <Divider sx={{ my: 1 }} />
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Quotas</Typography>
                                </Grid>
                                {plan.module === 'assetsmanagement.ai' && (
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Cloud Storage Size (GB):</Typography>
                                    <Typography variant="body2">{plan.totalSizeInPlan ?? 'N/A'}</Typography>
                                  </Grid>
                                )}
                                {plan.module === 'beamonscreen.com' && (
                                  <>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">Total Number of screens:</Typography>
                                      <Typography variant="body2">{plan.totalScreens ?? 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">Total Number of Reels:</Typography>
                                      <Typography variant="body2">{plan.totalReels ?? 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">Total Number of Playlists:</Typography>
                                      <Typography variant="body2">{plan.totalPlaylists ?? 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">Cloud Space Size (GB):</Typography>
                                      <Typography variant="body2">{plan.cloudSpaceInSizeInGB ?? 'N/A'}</Typography>
                                    </Grid>
                                  </>
                                )}
                                {plan.module === 'instantupdate.ai' && (
                                  <>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">WhatsApp Messages / Day:</Typography>
                                      <Typography variant="body2">{plan.whatsApp?.perDayLimit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">WhatsApp Messages Total Credit:</Typography>
                                      <Typography variant="body2">{plan.whatsApp?.totalCredit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">WhatsApp Templates / Day:</Typography>
                                      <Typography variant="body2">{plan.whatsAppTemplate?.perDayLimit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">WhatsApp Templates Total Credit:</Typography>
                                      <Typography variant="body2">{plan.whatsAppTemplate?.totalCredit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">WA Business Messages / Day:</Typography>
                                      <Typography variant="body2">{plan.whatsAppBusiness?.perDayLimit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">WA Business Messages Total Credit:</Typography>
                                      <Typography variant="body2">{plan.whatsAppBusiness?.totalCredit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">WA Business Templates / Day:</Typography>
                                      <Typography variant="body2">{plan.whatsAppBusinessTemplate?.perDayLimit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">WA Business Templates Total Credit:</Typography>
                                      <Typography variant="body2">{plan.whatsAppBusinessTemplate?.totalCredit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">GSM Text Messages / Day:</Typography>
                                      <Typography variant="body2">{plan.gsmText?.perDayLimit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">GSM Text Messages Total Credit:</Typography>
                                      <Typography variant="body2">{plan.gsmText?.totalCredit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">GSM Text Templates / Day:</Typography>
                                      <Typography variant="body2">{plan.gsmTextTemplate?.perDayLimit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">GSM Text Templates Total Credit:</Typography>
                                      <Typography variant="body2">{plan.gsmTextTemplate?.totalCredit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">Telegram Messages / Day:</Typography>
                                      <Typography variant="body2">{plan.telegram?.perDayLimit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">Telegram Messages Total Credit:</Typography>
                                      <Typography variant="body2">{plan.telegram?.totalCredit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">Telegram Templates / Day:</Typography>
                                      <Typography variant="body2">{plan.telegramTemplate?.perDayLimit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">Telegram Templates Total Credit:</Typography>
                                      <Typography variant="body2">{plan.telegramTemplate?.totalCredit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">Emails / Day:</Typography>
                                      <Typography variant="body2">{plan.email?.perDayLimit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">Emails Total Credit:</Typography>
                                      <Typography variant="body2">{plan.email?.totalCredit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">Email Templates / Day:</Typography>
                                      <Typography variant="body2">{plan.emailTemplate?.perDayLimit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">Email Templates Total Credit:</Typography>
                                      <Typography variant="body2">{plan.emailTemplate?.totalCredit ?? 'UNLIMITED'}</Typography>
                                    </Grid>
                                  </>
                                )}
                                {plan.module === 'instantupdate.ai' && (
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Sender Header Name:</Typography>
                                    <Typography variant="body2">{plan.senderHeaderName ?? '640807'}</Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Company Members */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <People color="primary" />
                  Company Members ({members.length})
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {membersLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={28} />
                  </Box>
                )}

                {membersError && (
                  <Alert severity="error" sx={{ mb: 1 }}>{membersError}</Alert>
                )}

                {!membersLoading && !membersError && members.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2, fontStyle: 'italic' }}>
                    No members found for this company.
                  </Typography>
                )}

                {!membersLoading && members.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {members.map((member) => (
                      <Card key={member._id} variant="outlined" sx={{
                        borderLeft: member.isPrimary ? '4px solid' : '1px solid',
                        borderLeftColor: member.isPrimary ? 'warning.main' : 'divider',
                      }}>
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 36, height: 36, fontSize: '0.85rem', bgcolor: 'secondary.main' }}>
                              {getInitials(member.profile.firstName, member.profile.lastName)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle2" noWrap sx={{ fontWeight: member.isPrimary ? 700 : 500 }}>
                                  {member.profile.firstName} {member.profile.lastName}
                                </Typography>
                                {member.isPrimary && (
                                  <Chip icon={<Star sx={{ fontSize: 14 }} />} label="Owner" size="small" color="warning" variant="outlined"
                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }} />
                                )}
                                <Chip label={member.status?.toUpperCase() || 'N/A'} size="small"
                                  color={getStatusColor(member.status)}
                                  sx={{ height: 20, fontSize: '0.65rem' }} />
                              </Box>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {member.email}{member.mobile ? ` • ${member.mobile}` : ''}
                              </Typography>
                            </Box>
                          </Box>
                          {member.moduleAndRole.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, ml: 6.5 }}>
                              {member.moduleAndRole.map((mr, idx) => (
                                <Chip key={idx} label={`${mr.module}: ${mr.role}`} size="small"
                                  color={mr.role === 'admin' ? 'warning' : 'default'}
                                  sx={(theme) => ({
                                    fontWeight: 'bold',
                                    fontSize: '0.7rem',
                                    height: 22,
                                    ...(mr.role !== 'admin' && {
                                      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
                                      color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
                                    }),
                                  })} />
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* System Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  System Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created At
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(company.createdAt)}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Last Updated
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(company.updatedAt)}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Default Bot Manager
                      </Typography>
                      <Typography variant="body1">
                        {company.configurations?.defaultBotManager || 'Platform Managed'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created By
                      </Typography>
                      <Typography variant="body1">
                        {company.createdBy || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Modified By
                      </Typography>
                      <Typography variant="body1">
                        {company.modifiedBy || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>

                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} variant="outlined" color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
